// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type StrongOmit,
  type Facility,
  type Project,
  type ProjectCreate,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { Button, Loader, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useUncontrolled } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { isNotNil } from 'helpers/isNotNil';
import { useCheckIfProjectIdentifierExists } from 'modules/Admin/Projects/useCheckIfProjectIdentifierExists';

type Props = {
  onProjectCreated: (project: Project) => void;
  editing: boolean;
  initialProjectName?: string;
  opened?: boolean;
  onClose?: () => void;
};

type FormBody = StrongOmit<ProjectCreate, 'defaultFacilityId'> & {
  defaultFacility: Pick<Facility, 'facilityId' | 'facilityName'> | null;
};

export const CreateProjectForm = ({ onProjectCreated, initialProjectName }: Props) => {
  const { checkIfProjectIdentifierExists, loading: checkingIdentifier } = useCheckIfProjectIdentifierExists();
  const { apiCall: createCall, loading: saving } = useEvolvePost('shop/project');
  const { data: projectTypes = [], loading: loadingProjectTypes } = useEvolveGet('shop/project/projectType', {
    lazy: false,
  });

  const facilitiesPaginatedGet = useEvolvePaginatedGet('shop/facility');

  const [projectIdentifierExists, setProjectIdentifierExists] = useState(false);

  const form = useForm<FormBody>({
    initialValues: {
      projectName: initialProjectName ?? '',
      projectIdentifier: '',
      projectType: '',
      defaultFacility: null,
    },
    validate: {
      projectName: (v) => !v,
      projectIdentifier: (v) => !v || projectIdentifierExists || checkingIdentifier,
    },
  });

  const onSubmit = async ({ defaultFacility, ...values }: typeof form.values) => {
    await createCall({
      ...values,
      defaultFacilityId: defaultFacility?.facilityId,
    }).then((newProject) => {
      onProjectCreated(newProject);
      notifications.show({
        title: 'Successfully created',
        message: `Created project ${newProject.projectName}.`,
        color: 'green',
      });
    });
  };

  useFormModal(form, onSubmit);

  const projectTypeIsNew = !!form.values.projectType && !projectTypes.includes(form.values.projectType);

  return (
    <>
      <TextInput
        label="Project Name"
        maxLength={64}
        autoFocus
        data-autofocus
        {...form.getInputProps('projectName')}
        required
      />
      <TextInputDebounced
        label="Project Id"
        maxLength={30}
        description="Must be unique across all projects"
        required
        {...form.getInputProps('projectIdentifier')}
        onChange={(v) => {
          form.setFieldValue('projectIdentifier', v);
          void checkIfProjectIdentifierExists(v).then(setProjectIdentifierExists);
        }}
        rightSection={checkingIdentifier ? <Loader size="sm" /> : null}
        error={
          projectIdentifierExists && !saving
            ? 'Project Id already used.'
            : form.getInputProps('projectIdentifier').error
        }
      />
      <SearchableSelect<'shop/facility', Facility, 'facilityId'>
        label="Default Facility"
        paginatedGet={facilitiesPaginatedGet}
        idKey="facilityId"
        getItemLabel={(f) => f.facilityName}
        clearable
        autoSelectWhenOnlyOneOption
        {...form.getInputProps('defaultFacility')}
      />
      <WrappedSelect
        label="Project Type"
        data={projectTypes}
        clearable
        searchable
        creatable
        createLabel="Create new project type"
        createdValue={projectTypeIsNew ? form.values.projectType : undefined}
        loading={loadingProjectTypes}
        {...form.getInputProps('projectType')}
        onCreate={(v) => form.setFieldValue('projectType', v)}
      />
    </>
  );
};

export const CreateProjectModal = (props: Props) => {
  const [opened, setOpened] = useUncontrolled({
    value: props.opened,
    defaultValue: false,
    onChange: (value) => {
      if (!value) {
        props.onClose?.();
      }
    },
  });
  const openedWasPassed = isNotNil(props.opened);

  return (
    <>
      {!openedWasPassed && (
        <Button
          onClick={() => setOpened(true)}
          disabled={props.editing}
          leftSection={<EvolveIcon icon="Add" size="sm" />}
        >
          New project
        </Button>
      )}
      <FormModal
        title="New project"
        opened={opened}
        onClose={() => setOpened(false)}
        confirmationText="Create"
        type="submit"
      >
        <CreateProjectForm {...props} />
      </FormModal>
    </>
  );
};
